import { CreditCardIcon } from '@heroicons/react/24/outline';
import SideBarElement, {
  SideBarElementItemProps,
} from '@/components/shared/sidebar/SidebarElement';
import SidebarMenu from '@/components/shared/sidebar/SidebarMenu';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { selectUserCurrency } from '@/store/selectors/userSelectors';
import { selectShowFluidQuickDeposit, selectUiIsSingleGame } from '@/store/ui/selectors';
import { useCashierShow } from '@/features/cashier/useCashier';
import {
  selectUserBalance,
  selectWalletError,
  selectWalletLoading,
} from '@/store/wallet/selectors';
import { getUserWallet } from '@/store/wallet/actions';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import Spinner from '@/components/UI/Spinner';
import Currency from '@/components/UI/Currency';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

const SidebarUser = () => {
  const intl = useIntl();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const showQuickDeposit = useSelector(selectShowFluidQuickDeposit);
  const showCashier = useCashierShow();
  const walletError = useSelector(selectWalletError);
  const balance = useSelector(selectUserBalance);
  const currency = useSelector(selectUserCurrency);
  const walletLoading = useSelector(selectWalletLoading);
  const isSingleGame = useSelector(selectUiIsSingleGame);
  const isRestricted = useRestrictedMode();

  if (!isAuthenticated) return null;

  const cashierOnClick = () => {
    if (walletError) {
      dispatch(getUserWallet());
      return;
    }
    triggerGTag(GTagEvents.header_open_cashier);
    showQuickDeposit && !isRestricted
      ? showCashier({ transactionType: 'quick-deposit' })
      : showCashier();
  };

  const sidebarElements: SideBarElementItemProps[] = [
    {
      'aria-label': 'Cashier button sidebar',
      'aria-roledescription': 'Cashier button used to open user wallet',
      'data-cy': 'fluid-cashier',
      id: 'cashier',
      onClick: cashierOnClick,
      shouldShow: isAuthenticated,
      icon: <CreditCardIcon width={30} height={30} />,
      // On the game page the balance is out of sync with the game iframe, so
      // show a static "Cashier" label instead of the (stale) figure.
      children: isSingleGame ? (
        intl.formatMessage({ id: 'header.cashier' })
      ) : walletLoading ? (
        <div className="flex items-center">
          <Spinner height={16} width={16} borderwidth={3} margin="0" color="blue2" />
        </div>
      ) : (
        <Currency currency={currency ?? ''} amount={balance} />
      ),
    },
  ];

  return (
    <>
      <div className="w-[80%] border-b-[1px] border-blue-light/30 mx-auto mb-2 mt-12" />
      <SidebarMenu>
        {sidebarElements.map(({ children, id, ...elementProps }) => (
          <SideBarElement key={`sidebar-cashier-element-${id}`} {...elementProps}>
            {children}
          </SideBarElement>
        ))}
      </SidebarMenu>
    </>
  );
};

export default SidebarUser;
